import React, { CSSProperties, FC } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { EyeSolidIcon } from '@/atoms/Icons/EyeSolidIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { CaptionXS } from '@/atoms/Text'
import { MomentUserStory } from '@/services/MomentsService/types'
import { getStoryThumbnailTransform } from '@/utils/Cloudinary/cloudinary'
import { hasNoStory, isTextStory, isVideoStory } from '@/utils/StoryUtil'

interface StoryCardProps {
  style?: CSSProperties
  className?: string
  story: MomentUserStory
  onClick: () => void
  hasVideoStory?: boolean
  isInfiniteView?: boolean
}

export const StoryCard: FC<StoryCardProps> = ({ className, onClick, story, style, hasVideoStory, isInfiniteView }) => {
  const typeCheck = { text: story?.text, videoUri: story?.videoUri }
  const isText = isTextStory(typeCheck)

  if (story === null || hasNoStory(typeCheck)) return null

  return (
    <div
      style={style}
      key={story.id}
      className={classNames(
        'group-story-thumbnail relative rounded-xl overflow-hidden cursor-pointer',
        {
          'border border-core-gray-800 bg-core-gray-900': isText,
          '!max-w-full': !hasVideoStory,
          'flex content-start justify-center': isInfiniteView && isText,
        },
        className,
      )}
      onClick={onClick}
    >
      <Switch>
        <Case condition={isVideoStory(typeCheck)}>
          <div
            className="aspect-h-16 aspect-w-9 min-h-full bg-cover"
            style={{
              backgroundImage: `url(${getStoryThumbnailTransform({
                path: story.posterCloudinaryPath as string,
              })})`,
            }}
          />
          <div className="absolute left-1/2 top-1/2 hidden h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/20 backdrop-blur-md group-story-thumbnail-hover:block">
            <PlayIcon className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
        </Case>
        <Case condition={isText}>
          <div
            className={classNames(
              hasVideoStory ? 'aspect-h-16 aspect-w-9' : 'w-20 min-h-[144px]',
              'flex overflow-hidden',
            )}
          >
            <div className="flex flex-col px-1.5 py-4">
              <div
                className="mx-auto mb-2 h-8 w-8 shrink-0 grow-0 overflow-hidden rounded-full border-black bg-cover"
                style={{ backgroundImage: `url(${story?.user?.profile?.image})` }}
                title="name"
              />
              <div className="flex">
                <div
                  className={classNames(
                    'my-auto line-clamp-5 w-14 overflow-hidden sm:line-clamp-4 lg:line-clamp-5 xl:line-clamp-4 break-words',
                    {
                      'w-[65px] !line-clamp-[7]': isInfiniteView,
                    },
                  )}
                >
                  <CaptionXS color="white" italic className={classNames('text-center', { 'text-xs': isInfiniteView })}>
                    &ldquo;{story.text}
                  </CaptionXS>
                </div>
                <CaptionXS color="white" italic className="self-end">
                  &rdquo;
                </CaptionXS>
              </div>
              <div className="absolute left-1/2 top-1/2 hidden h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/20 backdrop-blur-md group-story-thumbnail-hover:block">
                <EyeSolidIcon className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>
        </Case>
      </Switch>
    </div>
  )
}
